import { useState } from "react";
import PropTypes from "prop-types";
import { Users, RefreshCw, Mail, CheckCircle, CreditCard, Pencil, Trash2, Download, UserRound, UsersRound } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { dealerApi } from "../../utils/dealerApi";
import { EditCustomerModal } from "../shared/EditCustomerModal";
import { serviceTypeLabel } from "../../utils/serviceTypeLabel";

const DealerCustomersList = ({
  customers,
  fetchCustomers,
  t,
  dealerToken,
  scope = "default",
  onScopeChange,
  dealerRole = "dealer",
}) => {
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDelete = async (c) => {
    // Defence-in-depth: backend now enforces these guards in
    // /api/dealer/customers/{id}. We mirror them client-side to give
    // the dealer an instant message instead of a round-trip 423.
    // Fabrizio CR 23/02/2026 — accidental delete of an activated-card
    // customer revealed that the legacy guard only checked
    // contract_signed, missing the case where the customer had
    // already accepted terms / activated the wallet card.
    const cardActivated = ["activated", "active", "issued", "delivered"].includes(
      (c.card_status || "").toLowerCase()
    );
    if (c.contract_signed || c.terms_accepted || cardActivated) {
      const reasonLabel = c.contract_signed
        ? (t.cannotDeleteContractSigned || "contratto gia firmato")
        : cardActivated
          ? (t.cannotDeleteCardActivated || "card wallet gia attivata")
          : (t.cannotDeleteTermsAccepted || "cliente ha iniziato l'attivazione");
      toast.error(
        (t.cannotDeleteLifecycle || "Impossibile eliminare: {reason}. Contatta l'admin LoJack se necessario.")
          .replace("{reason}", reasonLabel),
        { duration: 6000 }
      );
      return;
    }
    const fullName = `${c.first_name || ''} ${c.last_name || ''}`.trim();
    const confirmMsg = (t.confirmDeleteCustomer || "Eliminare il cliente {name} ({email})?")
      .replace('{name}', fullName)
      .replace('{email}', c.email);
    if (!window.confirm(confirmMsg)) return;
    const reason = window.prompt(
      t.deletionReasonPrompt || "Motivo dell'eliminazione (richiesto, salvato nei log):",
      t.deletionReasonDefault || "Errore di inserimento dati / duplicato"
    );
    if (!reason || !reason.trim()) {
      toast.error(t.deletionReasonRequired || "Motivo obbligatorio. Eliminazione annullata.");
      return;
    }
    try {
      // Use the new dealer-scoped endpoint that enforces the
      // lifecycle guards server-side. The admin endpoint
      // (DELETE /api/customers/{id}) is no longer callable by
      // dealers — it requires an admin Bearer token.
      await axios.delete(
        `${API}/dealer/customers/${c.id}`,
        { params: { token: dealerToken, reason: reason.trim() } }
      );
      toast.success(t.customerDeletedSuccess || "Cliente eliminato");
      fetchCustomers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (status === 423 && detail && typeof detail === "object") {
        toast.error(detail.message || (t.cannotDeleteLifecycle || "Impossibile eliminare"), { duration: 7000 });
        return;
      }
      toast.error(detail?.message || detail || (t.customerDeleteError || "Errore eliminazione cliente"));
    }
  };

  /**
   * Download signed contract PDF for a customer of this dealer.
   * Req. Fabrizio 09/06/2026 — self-service archive for the dealer
   * (avoids waiting for the post-OTP email). The backend validates
   * dealer-ownership and returns 404 for cross-dealer attempts.
   */
  const handleDownloadSignedPdf = async (c) => {
    if (!c.contract_signed) {
      toast.error(t.contractNotSignedYet || "Contratto non ancora firmato dal cliente");
      return;
    }
    try {
      setDownloadingId(c.id);
      const res = await dealerApi.get(
        `/dealer/customers/${c.id}/signed-contract.pdf`,
        { token: dealerToken, config: { responseType: "blob" } },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fullName = `${c.first_name || ''} ${c.last_name || ''}`.trim().replace(/\s+/g, '_') || c.id;
      link.download = `Contratto_Firmato_${fullName}_${(c.id || '').slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t.signedPdfDownloaded || "PDF firmato scaricato");
    } catch {
      toast.error(t.signedPdfDownloadError || "Errore download PDF firmato");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-lg font-bold text-white">{t.myCustomers} ({customers.length})</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Scope toggle — gives sellers a way to see all of the
              dealer's customers (e.g. a Polish seller looking up a
              record created by the Italian manager) and gives managers
              a way to narrow the view to just their own attributions.
              Hidden for back-office (BO has free movement by design). */}
          {onScopeChange && dealerRole !== "back_office" && (
            <div
              className="inline-flex rounded-lg border border-gray-700 overflow-hidden text-xs"
              data-testid="dealer-customers-scope-toggle"
            >
              <button
                type="button"
                onClick={() => onScopeChange("mine")}
                className={`px-3 py-1.5 flex items-center gap-1.5 transition-colors ${
                  scope === "mine"
                    ? "bg-lojack-blue-500 text-white"
                    : "bg-transparent text-gray-400 hover:bg-gray-800"
                }`}
                data-testid="dealer-customers-scope-mine"
              >
                <UserRound size={12} />
                {t.scopeMineLabel || "Solo i miei"}
              </button>
              <button
                type="button"
                onClick={() => onScopeChange("all")}
                className={`px-3 py-1.5 flex items-center gap-1.5 transition-colors ${
                  scope === "all"
                    ? "bg-lojack-blue-500 text-white"
                    : "bg-transparent text-gray-400 hover:bg-gray-800"
                }`}
                data-testid="dealer-customers-scope-all"
              >
                <UsersRound size={12} />
                {t.scopeAllLabel || "Tutti del dealer"}
              </button>
            </div>
          )}
          <button
            onClick={() => fetchCustomers()}
            className="btn-lojack-outline text-sm flex items-center gap-2"
            data-testid="dealer-customers-refresh"
          >
            <RefreshCw size={14} /> {t.refresh}
          </button>
        </div>
      </div>
      {customers.length === 0 ? (
        <div className="wallet-card text-center py-8"><Users size={40} className="text-gray-700 mx-auto mb-3" /><p className="text-gray-500">{t.noCustomers}</p></div>
      ) : (
        <div className="space-y-2">
          {customers.map(c => (
            <div key={c.id} className="wallet-card flex items-center justify-between" data-testid={`dealer-customer-row-${c.id}`}>
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-white font-medium">{c.first_name || ''} {c.last_name || ''} <span className="text-gray-500 font-normal">| {c.email}</span></p>
                  <p className="text-gray-500 text-xs">{c.vehicle_brand} {c.vehicle_model} | {c.vehicle_plate || '-'} | {serviceTypeLabel(c.service_type)} | {c.country}</p>
                  {/* Ownership badge — surfaces who the customer is
                      attributed to (seller name OR "Manager" for the
                      legacy seller_id=None bucket). Always shown so an
                      ops user immediately understands *why* a record
                      may or may not be in their default scope. */}
                  <p
                    className="text-gray-500 text-[10px] mt-1 flex items-center gap-1"
                    data-testid={`dealer-customer-owner-${c.id}`}
                  >
                    {c.assigned_to_manager ? (
                      <>
                        <UserRound size={10} className="text-lojack-clause-400" />
                        <span className="text-lojack-clause-400">
                          {t.assignedToManager || "Cliente del manager"}
                        </span>
                      </>
                    ) : (
                      <>
                        <UserRound size={10} className="text-lojack-blue-400" />
                        <span className="text-lojack-blue-400">
                          {(t.assignedTo || "Assegnato a")} {c.assigned_seller_name || "—"}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-2 py-1 rounded-full text-xs ${c.email_status === 'sent' || c.email_status === 'delivered' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-800 text-gray-500'}`}><Mail size={10} className="inline mr-1" />{c.email_status}</span>
                {c.contract_signed && <span className="px-2 py-1 rounded-full text-xs bg-lojack-blue-500/20 text-lojack-blue-400"><CheckCircle size={10} className="inline mr-1" />{t.signed}</span>}
                {(c.card_status === 'created' || c.card_status === 'added_to_wallet') && <span className="px-2 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400"><CreditCard size={10} className="inline mr-1" />{t.card}</span>}
                <button
                  onClick={() => setEditingCustomer(c)}
                  className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
                  title={c.contract_signed ? (t.viewSignedReadonly || "Visualizza dati (firmato, sola lettura)") : (t.editCustomer || "Modifica cliente")}
                  data-testid={`dealer-edit-customer-${c.id}`}
                >
                  <Pencil size={16} className={c.contract_signed ? "text-gray-600" : "text-gray-400 hover:text-lojack-clause-400"} />
                </button>
                {c.contract_signed && (
                  <button
                    onClick={() => handleDownloadSignedPdf(c)}
                    disabled={downloadingId === c.id}
                    className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                    title={t.downloadSignedPdf || "Scarica contratto firmato (PDF)"}
                    data-testid={`dealer-download-signed-pdf-${c.id}`}
                  >
                    <Download size={16} className={`text-gray-400 hover:text-lojack-blue-400 ${downloadingId === c.id ? 'animate-pulse' : ''}`} />
                  </button>
                )}
                {!c.contract_signed && (
                  <button
                    onClick={() => handleDelete(c)}
                    className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
                    title={t.deleteCustomerOnlyUnsigned || "Elimina cliente (solo se non firmato)"}
                    data-testid={`dealer-delete-customer-${c.id}`}
                  >
                    <Trash2 size={16} className="text-gray-400 hover:text-red-400" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          onClose={() => setEditingCustomer(null)}
          onSuccess={() => { setEditingCustomer(null); fetchCustomers(); }}
          dealerToken={dealerToken}
        />
      )}
    </div>
  );
};

export default DealerCustomersList;

DealerCustomersList.propTypes = {
  customers: PropTypes.arrayOf(PropTypes.object),  // may be undefined while loading
  fetchCustomers: PropTypes.func.isRequired,       // required for reload after edit/delete
  t: PropTypes.object.isRequired,                  // translation pack — strings used inline throughout
  dealerToken: PropTypes.string,                   // dealer JWT for authenticated downloads
  scope: PropTypes.oneOf(["default", "mine", "all"]),
  onScopeChange: PropTypes.func,                   // when present, the scope toggle is rendered
  dealerRole: PropTypes.string,                    // "dealer" / "seller" / "back_office"
};
